import { useAuth } from "../App";
import { Shield, Zap, Users, ChevronRight } from "lucide-react";

const LandingPage = () => {
  const { login, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-stark-void flex items-center justify-center">
        <div className="arc-loader"></div>
      </div>
    );
  }

  if (user) {
    window.location.href = '/dashboard';
    return null;
  }

  return (
    <div className="min-h-screen bg-stark-void relative overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 240, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 240, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Hero Section */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="glass border-b border-arc-cyan/20 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border-2 border-arc-cyan flex items-center justify-center glow-cyan">
                <Zap className="w-5 h-5 text-arc-cyan" />
              </div>
              <div>
                <span className="font-display text-xl text-arc-cyan uppercase tracking-widest">
                  VISTA
                </span>
                <p className="font-mono text-[10px] text-arc-cyan/60 tracking-wide">
                  Visually Integrated System for Task Awareness
                </p>
              </div>
            </div>
            <div className="font-mono text-xs text-arc-cyan/60 hidden sm:block">
              v3.14.159 // VISTA_COMMAND_CENTER
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-4xl w-full">
            {/* Title Section */}
            <div className="text-center mb-12">
              <p className="font-mono text-arc-cyan/60 text-xs uppercase tracking-[0.3em] mb-4">
                [ System Online ]
              </p>
              <h1 className="font-display text-5xl md:text-7xl text-holo-white uppercase tracking-tight mb-6">
                Project <span className="text-arc-cyan">Command Center</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
                Advanced project management interface for elite development teams. 
                Coordinate, prioritize, and execute with precision.
              </p>
            </div>

            {/* Login Button */}
            <div className="text-center mb-12">
              <button
                onClick={login}
                data-testid="authorize-access-btn"
                className="group relative inline-flex items-center gap-3 px-8 py-4 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan font-display uppercase tracking-widest text-sm transition-all duration-300 hover:bg-arc-cyan hover:text-stark-void glow-cyan-hover clip-chamfer"
              >
                <Shield className="w-5 h-5" />
                <span>Authorize Access</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="mt-4 text-xs text-muted-foreground font-mono">
                // Authenticate via Google to initialize system
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-6">
              <FeatureCard
                icon={<Shield className="w-6 h-6" />}
                title="Secure Access"
                description="Google OAuth authentication with encrypted sessions"
              />
              <FeatureCard
                icon={<Users className="w-6 h-6" />}
                title="Team Sync"
                description="Assign tasks, add comments, collaborate in real-time"
              />
              <FeatureCard
                icon={<Zap className="w-6 h-6" />}
                title="Priority Matrix"
                description="Visual priority system for optimal task execution"
              />
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="glass border-t border-arc-cyan/20 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-xs font-mono text-arc-cyan/40">
            <span>VISTA // TASK COMMAND CENTER</span>
            <span>ENCRYPTED_CHANNEL_ACTIVE</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="group glass p-6 border border-arc-cyan/20 hud-corners transition-all duration-300 hover:border-arc-cyan/50 hover:bg-arc-cyan/5">
    <div className="text-arc-cyan mb-4">{icon}</div>
    <h3 className="font-display text-sm uppercase tracking-widest text-holo-white mb-2">
      {title}
    </h3>
    <p className="text-sm text-muted-foreground">{description}</p>
  </div>
);

export default LandingPage;
